import type { WebContainer } from '@webcontainer/api';
import type { HotEditSelection } from '../stores/hotEdit';
import type { PendingEdit } from './pendingEdits';
import {
  persistJsxAttributeLiteral,
  persistMergedClassName,
  persistSelectOptions,
  persistDataFile,
  persistIconSwap,
  persistImageReplace,
  persistTextEdit,
  persistThemeToken,
} from './applyVisualEdit';

/**
 * Apply: writes every pending change in one pass (Prototype Editor UX plan, step 2).
 *
 * Order matters — two changes can land in the same file, and each one is written against what
 * the one before it left — so the edits run one after another, never in parallel. The first
 * failure stops the pass and hands back what is left, so Retry starts from the change that
 * failed instead of writing the earlier ones twice.
 */
export type ApplyResult =
  | { ok: true; written: number }
  | { ok: false; written: number; remaining: PendingEdit[]; error: string; failed: PendingEdit };

export type PersistFns = {
  className: typeof persistMergedClassName;
  text: typeof persistTextEdit;
  attribute: typeof persistJsxAttributeLiteral;
  options: typeof persistSelectOptions;
  theme: typeof persistThemeToken;
  image: typeof persistImageReplace;
  icon: typeof persistIconSwap;
  data: typeof persistDataFile;
};

const DEFAULT_PERSIST: PersistFns = {
  className: persistMergedClassName,
  text: persistTextEdit,
  attribute: persistJsxAttributeLiteral,
  options: persistSelectOptions,
  theme: persistThemeToken,
  image: persistImageReplace,
  icon: persistIconSwap,
  data: persistDataFile,
};

/** Style changes on a list row land on the row's shared template, as the "Changes all 5" chip says. */
function listContextFor(edit: PendingEdit, scope: 'item' | 'all'): HotEditSelection['listContext'] {
  if (edit.kind === 'class' && scope === 'all') return null;
  return edit.selection?.listContext ?? null;
}

async function writeOne(edit: PendingEdit, wc: WebContainer | null, scope: 'item' | 'all', fns: PersistFns) {
  // A design system colour is written to the stylesheet, so it needs no line in a component.
  if (edit.kind === 'theme') return fns.theme({ wc, token: edit.token, value: edit.value });
  // A data change is a whole file the grid has already composed.
  if (edit.kind === 'data') return fns.data({ wc, filePath: edit.filePath, content: edit.content });

  const sel = edit.selection;
  const source = sel?.source;
  if (!sel || !source?.filePath) {
    return { ok: false as const, error: "The editor can't tell where this comes from in the code." };
  }
  const common = { wc, filePath: source.filePath, lineNumber: source.lineNumber };

  switch (edit.kind) {
    case 'class':
      return fns.className({
        ...common,
        cnAppendOnly: edit.classDelta,
        cnRemove: edit.classRemove,
        cnKeepConflicting: edit.keepConflicting,
        columnNumber: source.columnNumber,
        tagName: sel.tagName,
        componentName: sel.ownerComponentName,
        listContext: listContextFor(edit, scope),
      });
    case 'text':
      return fns.text({
        ...common,
        text: edit.text,
        columnNumber: source.columnNumber,
        tagName: sel.tagName,
        componentName: sel.ownerComponentName,
        hintText: sel.textContent,
        listContext: sel.listContext,
      });
    case 'attr':
      return fns.attribute({ ...common, attributeName: edit.attribute, literalValue: edit.value });
    case 'options':
      return fns.options({
        ...common,
        options: edit.options,
        columnNumber: source.columnNumber,
        tagName: sel.tagName,
      });
    case 'icon':
      return fns.icon({
        ...common,
        iconName: edit.iconName,
        columnNumber: source.columnNumber,
      });
    case 'image':
      return fns.image({
        ...common,
        reference: edit.reference,
        fromStorePath: edit.fromStorePath,
        recordId: sel.listContext?.reactKey ?? sel.listRow?.key ?? null,
      });
  }
}

export async function applyEdits(args: {
  edits: readonly PendingEdit[];
  wc: WebContainer | null;
  /** `all` writes a row's style on the shared template; `item` keeps it on the row. */
  listScope?: 'item' | 'all';
  persist?: PersistFns;
}): Promise<ApplyResult> {
  const { edits, wc, listScope = 'all', persist = DEFAULT_PERSIST } = args;
  let written = 0;

  for (let i = 0; i < edits.length; i++) {
    const edit = edits[i]!;
    const result = await writeOne(edit, wc, listScope, persist);
    if (!result.ok) {
      return {
        ok: false,
        written,
        remaining: edits.slice(i) as PendingEdit[],
        error: result.error,
        failed: edit,
      };
    }
    written += 1;
  }

  return { ok: true, written };
}
